import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { PaymentRequest } from '../types';

const PaymentRequests = () => {
  const navigate = useNavigate();
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [orderReferenceSearch, setOrderReferenceSearch] = useState('');
  const [transactionStatusFilter, setTransactionStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPaymentRequests = async (
    customerSearch: string = '',
    orderReference: string = '',
    page: number = 1,
    status: string = '',
    startDate: string = '',
    endDate: string = ''
  ) => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (customerSearch) query.append('customer_search', customerSearch);
      if (orderReference) query.append('order_reference', orderReference);
      if (status) query.append('status', status);
      if (startDate) query.append('start_date', startDate);
      if (endDate) query.append('end_date', endDate);
      query.append('page', String(page));
      query.append('per_page', '10');

      const res = await fetchWithAuth(`/delta_pay_payment_requests?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt')}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch payment requests');
      }

      const data = await res.json();
      setPaymentRequests(data.payment_requests);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      setPaymentRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentRequests(customerSearch, orderReferenceSearch, currentPage, transactionStatusFilter, startDate, endDate);
  }, [customerSearch, orderReferenceSearch, currentPage, transactionStatusFilter, startDate, endDate]);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const getTransactionStatusBadge = (status: string | null) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';

    switch (status) {
      case 'succeeded':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Succeeded</span>;
      case 'failed':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Failed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Pending</span>;
    }
  };

  const getRequestStatusBadge = (status: string | null) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';

    if (!status || status === 'pending') {
      return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
    }
    if (status === 'completed') {
      return <span className={`${baseClasses} bg-green-100 text-green-800`}>Completed</span>;
    }
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
  };

  const formatAmount = (amount: string) => {
    const num = parseFloat(amount);
    return isNaN(num) ? amount : `E${num.toFixed(2)}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Requests</h1>
        <p className="text-gray-600">Track all DeltaPay payment requests</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label htmlFor="customerSearch" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="customerSearch"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="orderReference" className="block text-sm font-medium text-gray-700 mb-1">
              Order Reference
            </label>
            <input
              type="text"
              id="orderReference"
              value={orderReferenceSearch}
              onChange={(e) => setOrderReferenceSearch(e.target.value)}
              placeholder="Order reference..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="transactionStatus" className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Status
            </label>
            <select
              id="transactionStatus"
              value={transactionStatusFilter}
              onChange={(e) => setTransactionStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="succeeded">Succeeded</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Results count */}
        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {totalCount} payment request{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Payment Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="hidden md:table-cell px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metadata UUID
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request Status
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction Status
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    Loading payment requests...
                  </td>
                </tr>
              ) : paymentRequests.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No payment requests found
                  </td>
                </tr>
              ) : (
                paymentRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="hidden md:table-cell px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(request.created_at)}
                    </td>
                    <td className="px-2 md:px-6 py-4 text-xs md:text-sm text-gray-900 font-mono">
                      {request.delta_pay_metadata_uuid}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      <button
                        onClick={() => navigate(`/orders/${request.order_id}`)}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                      >
                        {request.order_reference}
                      </button>
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {request.customer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.customer_code}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 md:px-6 py-4 text-sm text-gray-900">
                      {formatAmount(request.delta_pay_amount)}
                    </td>
                    <td className="px-2 md:px-6 py-4 text-sm text-gray-500">
                      {request.delta_pay_username}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      {getRequestStatusBadge(request.delta_pay_payment_request_status)}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      {getTransactionStatusBadge(request.delta_pay_transaction_status)}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      <button
                        onClick={() => navigate(`/payment-requests/${request.id}`)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentRequests;
